"use client";

import {
    autoUpdate,
    FloatingFocusManager,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useMergeRefs,
    useTypeahead,
} from "@floating-ui/react";
import cn from "clsx";
import React, { forwardRef, useId, useState } from "react";
import { FiAlertCircle, FiChevronDown } from "react-icons/fi";
import {
    FieldDescription,
    getFieldDescriptionId,
} from "../form-fields/field-description";
import { FieldLabel } from "../form-fields/field-label";
import { FieldProps } from "../form-fields/field-props";
import { LoadingSpinner } from "../loading-spinner";
import styles from "./SelectDropdown.module.scss";

export type SelectDropdownItem<T> = {
    value: T;
    label: string;
};

type T = any;

export type SelectDropdownProps<T> = FieldProps & {
    /**
     * The items to to display in the menu dropdown
     * @required
     */
    items: SelectDropdownItem<T>[];

    /**
     * Sets the selected item to the item with the given value
     */
    value?: string;

    /**
     * Sets the selected item to the item with the given value for the initial render
     */
    defaultValue?: string;

    /**
     * Callback function after an item has been selected
     * @param value the value of the selected item
     */
    onChange?: (value?: T) => void;

    /**
     * Placeholder text for the select
     * @default Select...
     */
    placeholder?: string;

    /**
     * Force into controlled component mode even if `value` is undefined
     * @default false
     */
    forceControlledMode?: boolean;
    /**
     * Additional styling for the wrapper
     */
    wrapperClassName?: string;
};

export const SelectDropdown = forwardRef<
    HTMLDivElement,
    SelectDropdownProps<T>
>(
    (
        {
            defaultValue,
            descriptionText,
            disabled,
            errorText,
            fieldId,
            hideErrorIcon,
            hideErrorText = false,
            hideLabel = false,
            hideMarker,
            items,
            label,
            loading,
            onChange,
            placeholder = "Select...",
            required = false,
            showOptionalValidationText,
            value,
            forceControlledMode,
            wrapperClassName,
            ...rest
        },
        ref,
    ) => {
        const hasError = !!errorText;
        const selectId = (fieldId || "") + useId();
        const labelId = useId();
        const hasDescription = !!descriptionText || hasError;

        const [isOpen, setIsOpen] = useState<boolean>(false);
        const [activeIndex, setActiveIndex] = useState<number | null>(null);

        const indexOfDefaultValueInItems = items.findIndex(
            (item) => item.value === defaultValue,
        );
        const [selectedIndex, setSelectedIndex] = useState<number | null>(
            indexOfDefaultValueInItems >= 0 ? indexOfDefaultValueInItems : null,
        );

        const isControlledComponent =
            typeof value !== "undefined" || forceControlledMode;
        const indexOfValueInItems = items.findIndex(
            (item) => item.value === value,
        );
        const isValueInItems = indexOfValueInItems >= 0;
        if (
            isControlledComponent &&
            isValueInItems &&
            selectedIndex !== indexOfValueInItems
        ) {
            setSelectedIndex(indexOfValueInItems);
        } else if (
            isControlledComponent &&
            !isValueInItems &&
            selectedIndex !== null
        ) {
            setSelectedIndex(null);
        }

        const { refs, floatingStyles, context } = useFloating({
            placement: "bottom-start",
            open: isOpen,
            onOpenChange: setIsOpen,
            whileElementsMounted: autoUpdate,
            middleware: [
                size({
                    apply({ elements }) {
                        Object.assign(elements.floating.style, {
                            maxHeight: "280px",
                        });
                    },
                }),
            ],
        });

        const listRef = React.useRef<Array<HTMLElement | null>>([]);
        const listContentRef = React.useRef(items.map((item) => item.label));
        const isTypingRef = React.useRef(false);

        const click = useClick(context, {
            enabled: !disabled,
        });
        const dismiss = useDismiss(context);
        const listNav = useListNavigation(context, {
            listRef,
            activeIndex,
            selectedIndex,
            onNavigate: setActiveIndex,
            openOnArrowKeyDown: !disabled,
        });
        const handleSelect = (index: number) => {
            setSelectedIndex(index);
            setIsOpen(false);
            onChange?.(items.at(index)?.value);
        };
        const typeahead = useTypeahead(context, {
            listRef: listContentRef,
            activeIndex,
            selectedIndex,
            onMatch: isOpen ? setActiveIndex : handleSelect,
            onTypingChange(isTyping) {
                isTypingRef.current = isTyping;
            },
        });

        const mergedRef = useMergeRefs([ref, refs.setReference]);

        const { getReferenceProps, getFloatingProps, getItemProps } =
            useInteractions([dismiss, listNav, typeahead, click]);

        const selectedItemLabel =
            selectedIndex !== null ? items[selectedIndex]?.label : undefined;

        const getOptionId = (index: number) => `${selectId}_option_${index}`;
        const listboxId = `${selectId}_listbox`;

        return (
            <div className={cn(styles.wrapper, wrapperClassName)}>
                {!hideLabel && (
                    <FieldLabel
                        required={required}
                        fieldId={selectId}
                        id={labelId}
                        hideMarker={hideMarker}
                        showOptionalValidationText={showOptionalValidationText}
                        disabled={disabled}
                        hasError={hasError}
                        onClick={() => {
                            !disabled &&
                                (
                                    refs.reference.current as HTMLDivElement
                                ).focus();
                        }}
                    >
                        {label}
                    </FieldLabel>
                )}
                <div
                    {...rest}
                    {...getReferenceProps()}
                    tabIndex={0}
                    ref={mergedRef}
                    id={selectId}
                    aria-activedescendant={
                        typeof selectedIndex === "number"
                            ? getOptionId(selectedIndex)
                            : ""
                    }
                    aria-controls={listboxId}
                    aria-describedby={
                        hasDescription
                            ? getFieldDescriptionId(selectId)
                            : undefined
                    }
                    aria-disabled={disabled || undefined}
                    aria-expanded={isOpen}
                    aria-invalid={hasError || undefined}
                    aria-labelledby={labelId}
                    aria-required={required || undefined}
                    className={cn(styles.container, {
                        [styles["container--has-selection"]]:
                            typeof selectedIndex === "number",
                    })}
                    role="combobox"
                >
                    <div className={styles.value}>
                        {selectedItemLabel || placeholder}
                    </div>
                    <span
                        className={cn([
                            styles.icon,
                            {
                                [styles["icon--disabled"]]: disabled,
                                [styles["icon--error"]]: hasError && !disabled,
                            },
                        ])}
                    >
                        {loading ? (
                            <LoadingSpinner
                                size="var(--g-size-xxs)"
                                aria-label="Loading"
                            />
                        ) : hasError && !disabled ? (
                            <FiAlertCircle
                                size={16}
                                aria-label="Error"
                                title="Error"
                            />
                        ) : (
                            <FiChevronDown
                                aria-label="Toggle menu"
                                aria-hidden="true"
                                size={16}
                            />
                        )}
                    </span>
                </div>
                <FloatingFocusManager
                    context={context}
                    modal={false}
                    disabled={disabled}
                >
                    <div
                        {...getFloatingProps()}
                        aria-hidden={!isOpen}
                        aria-labelledby={labelId}
                        className={styles.list}
                        id={listboxId}
                        ref={refs.setFloating}
                        role="listbox"
                        style={{
                            ...floatingStyles,
                            visibility: isOpen ? "visible" : "hidden",
                        }}
                    >
                        {items.map((item, index) => (
                            <div
                                {...getItemProps({
                                    onClick() {
                                        handleSelect(index);
                                    },
                                    onKeyDown(event) {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            handleSelect(index);
                                        }

                                        if (event.key === "Tab") {
                                            event.preventDefault();
                                            handleSelect(index);
                                        }

                                        if (
                                            event.key === " " &&
                                            !isTypingRef.current
                                        ) {
                                            event.preventDefault();
                                            handleSelect(index);
                                        }
                                    },
                                })}
                                key={`${item.value}${index}`}
                                id={getOptionId(index)}
                                ref={(node) => {
                                    listRef.current[index] = node;
                                }}
                                role="option"
                                tabIndex={index === activeIndex ? 0 : -1}
                                aria-selected={
                                    index === selectedIndex || undefined
                                }
                                data-active={index === activeIndex || undefined}
                                className={styles.item}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                </FloatingFocusManager>
                {hasDescription && (
                    <FieldDescription
                        fieldId={selectId}
                        errorText={errorText}
                        hideErrorIcon={hideErrorIcon}
                        hideErrorText={hideErrorText}
                        disabled={disabled}
                    >
                        {descriptionText}
                    </FieldDescription>
                )}
            </div>
        );
    },
);

SelectDropdown.displayName = "SelectDropdown";
